import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { getCurrentPhotoFillCoveredSize } from '@/lib/photos/photo-fill-resolution'
import { computePhotoFillOffsetAfterResize } from '@/lib/photos/photo-fill-resize-offset'
import { localVecToCanvas } from '@/lib/transforms/geometry'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import { isCornerResizeHandle } from '@/lib/transforms/unified-selection-oriented-resize'
import type { MultiSelectResizeShapeSnap } from '../types/multi-select-resize-shape-snap'
import type { MultiSelectResizePhotoConstraint } from '../types/multi-select-resize-photo-constraint'
import { getMultiResizeFixedAnchorLocal } from './multi-resize-local-frame'

/**
 * Caps a multi-select resize so no photo-filled shape in the selection exceeds its own
 * resolution ceiling. Every shape in the selection scales together by a single shared factor
 * (`sx`/`sy` in `computeMultiSelectResize`) — capping only the offending shape's own update
 * would detach it from the rest of the selection's proportional scaling, so instead this finds
 * the tightest violation across all constrained shapes and uniformly scales *every* shape's
 * update (and the chrome box shown to the user) down by that same factor, about the same fixed
 * anchor point `computeMultiSelectResize` used. A uniform scale about a fixed point commutes
 * with each shape's own rotation-frame re-projection, so this is exact regardless of whether
 * individual shapes are rotated differently from the chrome/selection.
 *
 * Also re-derives each photo-filled shape's `offsetX/offsetY` from its own before/after rect via
 * `computePhotoFillOffsetAfterResize` — see that function for the uniform-scale-vs-crop split —
 * using the final (post-ceiling-clamp) rect so the two adjustments compose correctly.
 */
export function clampMultiSelectResizeToPhotoResolution(
  result: { nextChrome: MultiSelectChrome; updates: Record<string, Partial<ShapeData>> },
  photoConstraintsById: Record<string, MultiSelectResizePhotoConstraint | undefined>,
  chromeStart: MultiSelectChrome,
  direction: UnifiedSelectionResizeHandle,
  w0: number,
  h0: number,
  shapeInitials: Record<string, MultiSelectResizeShapeSnap> = {}
): { nextChrome: MultiSelectChrome; updates: Record<string, Partial<ShapeData>> } {
  const { nextChrome, updates } = result
  let scale = 1

  for (const [id, constraint] of Object.entries(photoConstraintsById)) {
    if (!constraint) continue

    const update = updates[id]

    if (!update || update.width == null || update.height == null) continue

    if (constraint.maxWidth != null && update.width > constraint.maxWidth) {
      scale = Math.min(scale, constraint.maxWidth / update.width)
    }

    if (constraint.maxHeight != null && update.height > constraint.maxHeight) {
      scale = Math.min(scale, constraint.maxHeight / update.height)
    }
  }

  const isCornerHandle = isCornerResizeHandle(direction)

  /**
   * Re-derives offsetX/offsetY/scale for every photo-filled shape against its own initial rect.
   * For a non-corner (crop) resize, also caps that shape's own growth at its current covered
   * size — the same companion clamp `clampCropResizeToCoveredSize` applies for a single-shape
   * resize, re-implemented here as a simple recenter (rather than a directional reanchor) since a
   * multi-select update has no single handle to anchor an individual shape against.
   */
  function withPhotoFillOffsets(
    finalUpdates: Record<string, Partial<ShapeData>>
  ): Record<string, Partial<ShapeData>> {
    let out: Record<string, Partial<ShapeData>> | null = null

    for (const [id, constraint] of Object.entries(photoConstraintsById)) {
      if (!constraint?.photoFill) continue

      const update = finalUpdates[id]
      const initial = shapeInitials[id]

      if (!update || !initial || update.width == null || update.height == null) continue

      let next = {
        x: update.x ?? initial.x,
        y: update.y ?? initial.y,
        width: update.width,
        height: update.height,
      }

      if (!isCornerHandle) {
        const coveredSize = getCurrentPhotoFillCoveredSize(
          initial.width,
          initial.height,
          constraint.photoFill.naturalWidth,
          constraint.photoFill.naturalHeight,
          constraint.photoFill.scale
        )

        if (coveredSize) {
          const clampedWidth = Math.min(next.width, coveredSize.coveredWidth)
          const clampedHeight = Math.min(next.height, coveredSize.coveredHeight)

          if (clampedWidth !== next.width || clampedHeight !== next.height) {
            next = {
              x: next.x + (next.width - clampedWidth) / 2,
              y: next.y + (next.height - clampedHeight) / 2,
              width: clampedWidth,
              height: clampedHeight,
            }
          }
        }
      }

      const { offsetX, offsetY, scale } = computePhotoFillOffsetAfterResize({
        isCornerHandle,
        initial,
        next,
        offsetX: constraint.photoFill.offsetX,
        offsetY: constraint.photoFill.offsetY,
        scale: constraint.photoFill.scale,
        naturalWidth: constraint.photoFill.naturalWidth,
        naturalHeight: constraint.photoFill.naturalHeight,
      })

      out = out ?? { ...finalUpdates }
      out[id] = {
        ...update,
        ...next,
        photoFill: { ...constraint.photoFill, offsetX, offsetY, scale },
      }
    }

    return out ?? finalUpdates
  }

  if (scale >= 1) {
    const withOffsets = withPhotoFillOffsets(updates)

    return withOffsets === updates ? result : { nextChrome, updates: withOffsets }
  }

  const rot = chromeStart.rotationDeg
  const anchorLocal = getMultiResizeFixedAnchorLocal(direction, w0, h0)
  const anchorCanvas = {
    x: chromeStart.pivotX + localVecToCanvas(anchorLocal, rot).x,
    y: chromeStart.pivotY + localVecToCanvas(anchorLocal, rot).y,
  }

  const scaleBoxAboutAnchor = (
    center: { x: number; y: number },
    width: number,
    height: number
  ): { x: number; y: number; width: number; height: number } => {
    const newCenter = {
      x: anchorCanvas.x + (center.x - anchorCanvas.x) * scale,
      y: anchorCanvas.y + (center.y - anchorCanvas.y) * scale,
    }
    const newWidth = width * scale
    const newHeight = height * scale

    return {
      x: newCenter.x - newWidth / 2,
      y: newCenter.y - newHeight / 2,
      width: newWidth,
      height: newHeight,
    }
  }

  const scaledUpdates: Record<string, Partial<ShapeData>> = {}

  for (const [id, update] of Object.entries(updates)) {
    if (update.x == null || update.y == null || update.width == null || update.height == null) {
      scaledUpdates[id] = update
      continue
    }

    const center = { x: update.x + update.width / 2, y: update.y + update.height / 2 }

    scaledUpdates[id] = {
      ...update,
      ...scaleBoxAboutAnchor(center, update.width, update.height),
    }
  }

  const scaledChromeBox = scaleBoxAboutAnchor(
    { x: nextChrome.pivotX, y: nextChrome.pivotY },
    nextChrome.width,
    nextChrome.height
  )
  const scaledChrome: MultiSelectChrome = {
    pivotX: scaledChromeBox.x + scaledChromeBox.width / 2,
    pivotY: scaledChromeBox.y + scaledChromeBox.height / 2,
    width: scaledChromeBox.width,
    height: scaledChromeBox.height,
    rotationDeg: nextChrome.rotationDeg,
  }

  return { nextChrome: scaledChrome, updates: withPhotoFillOffsets(scaledUpdates) }
}
